import { useMutation } from '@apollo/client';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import LabelWithIndicator from 'components/atoms/LabelWithIndicator';
import LabelStyle from 'components/LabelStyle';
import {
  DisplayWebhook,
  EmptyWebhook,
  SetWebhook
} from 'components/molecules/IntegrateWebhook';
import {
  DisplayGuideHelper,
  DisplayIntegrationInfo
} from 'components/molecules/IntegrationsCrud';
import RayoToast from 'components/RayoToast';
import { UPDATE_WEBHOOK } from 'graphql/mutations/integrations';
import PropTypes from 'prop-types';
import React from 'react';
import toast from 'react-hot-toast';

const IntegrateWebhook = ({ selectedIntegration, refetch }) => {
  const theme = useTheme();
  const [isEnableCreate, setIsEnableCreate] = React.useState(false);
  const [webhook, setWebhook] = React.useState(
    selectedIntegration?.integrationsData
  );

  const [updateWebhook, { loading }] = useMutation(UPDATE_WEBHOOK);

  const handleUpdateWebhook = async (data) => {
    try {
      toast.loading('Guardando la configuración...', { id: 'updWebhook' });
      await updateWebhook({
        variables: { ...data, clientId: selectedIntegration?.clientId },
        onCompleted: () => {
          setWebhook(data);
          setIsEnableCreate(false);
        }
      });
      toast.success('Se ha guardado la configuración del WEBHOOK!', {
        id: 'updWebhook',
        duration: 5000
      });
      refetch();
    } catch (error) {
      toast(
        <RayoToast
          message="Algo salió mal, intentalo de nuevo!"
          err={error}
          id="updWebhook"
        />,
        {
          id: 'genApiKey',
          duration: Infinity
        }
      );
    }
  };

  return (
    <Grid container justifyContent="center" columnSpacing={2}>
      <Grid lg={8}>
        <Stack
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            height: '100%'
          }}
        >
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid lg={8}>
              <DisplayIntegrationInfo
                title={selectedIntegration?.title}
                imageUrl={selectedIntegration?.imageUrl}
                subtitle={
                  webhook?.webhookSecret || webhook?.webhookUrl ? (
                    <LabelWithIndicator>Integrado</LabelWithIndicator>
                  ) : (
                    <Typography variant="body2" color={theme.palette.grey[600]}>
                      No Integrado
                    </Typography>
                  )
                }
                description={selectedIntegration?.description}
              />
            </Grid>
            <Grid lg={4}>
              {selectedIntegration?.guideUrl && (
                <DisplayGuideHelper
                  integrationTitle={selectedIntegration.title}
                  urlHelper={selectedIntegration?.guideUrl}
                />
              )}
            </Grid>
            <Grid lg={12}>
              <Stack
                sx={{
                  maxHeight: '60vh',
                  minHeight: '50vh',
                  overflowY: 'auto',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 4
                }}
                spacing={2}
              >
                <Stack direction="row" justifyContent="space-between">
                  <LabelStyle>Integración disponible</LabelStyle>
                  {!isEnableCreate &&
                    (webhook.webhookSecret || webhook.webhookUrl) && (
                      <Button
                        variant="outlined"
                        onClick={() => setIsEnableCreate(true)}
                        startIcon={<EditIcon />}
                      >
                        Editar Webhook
                      </Button>
                    )}
                </Stack>
                {(!webhook?.webhookUrl && !webhook?.webhookSecret) ||
                isEnableCreate ? (
                  <Stack
                    sx={{
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2
                    }}
                    alignItems="center"
                    spacing={2}
                  >
                    {isEnableCreate ? (
                      <SetWebhook
                        {...{
                          webhook,
                          onCancel: () => setIsEnableCreate(false),
                          onSubmit: handleUpdateWebhook,
                          loading
                        }}
                      />
                    ) : (
                      <EmptyWebhook {...{ setIsEnableCreate }} />
                    )}
                  </Stack>
                ) : (
                  <DisplayWebhook {...{ webhook }} />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

IntegrateWebhook.propTypes = {
  selectedIntegration: PropTypes.shape({
    clientId: PropTypes.string,
    counter: PropTypes.number,
    description: PropTypes.string,
    guideUrl: PropTypes.string,
    id: PropTypes.string,
    imageUrl: PropTypes.string,
    indexReference: PropTypes.number,
    integrationsData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isEmphasized: PropTypes.bool,
    message: PropTypes.string,
    metadata: PropTypes.object,
    title: PropTypes.string
  }),
  refetch: PropTypes.func
};

export default IntegrateWebhook;
